/**
 * A cell frame (docs/spec/04 §3): 16 rows of 8 fat pixels. `.` is the background, `1` `2` `3` the
 * cave's earth, rock and bright colours. Each fat pixel is drawn two screen pixels wide.
 */
export type Frame = readonly string[];

export const FRAME_W = 8;
export const FRAME_H = 16;
export const CELL = 16;

/** Parse the `|`-separated form used by hand-drawn art. */
export function frame(rows: string): Frame {
  const out = rows.split('|');
  if (out.length !== FRAME_H || out.some((r) => r.length !== FRAME_W)) {
    throw new Error(`bad frame: ${out.length} rows, widths ${out.map((r) => r.length).join(',')}`);
  }
  return out;
}

/** Build a frame from a function of (x, y) giving '.', '1', '2' or '3'. */
export function paint(f: (x: number, y: number) => string): Frame {
  const rows: string[] = [];
  for (let y = 0; y < FRAME_H; y++) {
    let row = '';
    for (let x = 0; x < FRAME_W; x++) row += f(x, y);
    rows.push(row);
  }
  return rows;
}

export function mirror(f: Frame): Frame {
  return f.map((row) => [...row].reverse().join(''));
}

export function pixel(f: Frame, x: number, y: number): string {
  return f[y]?.[x] ?? '.';
}

/** A small, stable hash for procedural texture: the same (x, y, n) always gives the same bits. */
export function hash(x: number, y: number, n: number): number {
  let h = Math.imul(x + 374761393, 668265263) ^ Math.imul(y + 3266489917, 2246822519) ^ Math.imul(n + 1, 3266489917);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}
